class KiyohReviews extends HTMLElement {
    constructor() {
      super();
      this.template = this.querySelector('template');
      this.src = this.getAttribute('data-src');
      this.maxRetries = 3;
      this.retryDelay = 2000; // 2 seconds
      // Store template parts
      this.summaryTemplate = this.template?.content.querySelector('.kiyoh-summary')?.cloneNode(true);
      this.reviewTemplate = this.template?.content.querySelector('.review-slide-template')?.cloneNode(true);
    }
  
    connectedCallback() {
      this.loadAndRender();
    }
  
    async loadAndRender() {
      let retryCount = 0;
      
      const tryLoad = async () => {
        try {
          this.showLoading();
          const res = await fetch(this.src);
          
          if (!res.ok) {
            throw new Error(`HTTP error! status: ${res.status}`);
          }
          
          const xmlText = await res.text();
          const parser = new DOMParser();
          const xml = parser.parseFromString(xmlText, 'application/xml');
  
          // Check for XML parsing errors
          const parserError = xml.querySelector('parsererror');
          if (parserError) {
            throw new Error('XML parsing error');
          }
  
          const summary = {
            avgRating: this.#getText(xml, 'averageRating'),
            totalReviews: this.#getText(xml, 'numberReviews'),
            percentageRecommendation: this.#getText(xml, 'percentageRecommendation'),
            companyName: this.#getText(xml, 'locationName'),
            viewReviewUrl: this.#getText(xml, 'viewReviewUrl')
          };
  
          const reviewNodes = Array.from(xml.querySelectorAll('reviews > reviews')).slice(0, 10);
          const reviews = reviewNodes.map((reviewEl) => {
            const author = this.#getText(reviewEl, 'reviewAuthor');
            const city = this.#getText(reviewEl, 'city');
            const date = this.#getText(reviewEl, 'dateSince')?.split('T')[0];
            const rating = this.#getText(reviewEl, 'rating');
            const reviewId = this.#getText(reviewEl, 'reviewId');
            const contents = reviewEl.querySelectorAll('reviewContent > reviewContent');
  
            let oneliner = '';
            let opinion = '';
  
            contents.forEach(content => {
              const group = this.#getText(content, 'questionGroup');
              const value = this.#getText(content, 'rating');
  
              if (group === 'DEFAULT_ONELINER') oneliner = value;
              if (group === 'DEFAULT_OPINION') opinion = value;
            });
  
            return { author, city, date, rating, reviewId, oneliner, opinion };
          });
  
          if (reviews.length === 0) {
            throw new Error('No reviews found');
          }
  
          this.hideLoading(); // Hide loading before rendering
          this.#render(reviews, summary);
        } catch (error) {
          console.error('KiyohReviews error:', error);
          
          if (retryCount < this.maxRetries) {
            retryCount++;
            console.log(`Retrying (${retryCount}/${this.maxRetries})...`);
            setTimeout(tryLoad, this.retryDelay);
          } else {
            this.showError('Unable to load reviews at this time');
          }
        }
      };
  
      await tryLoad();
    }
  
    #getText(context, tag) {
      return context.querySelector(tag)?.textContent.trim() || '';
    }

    #setKiyohAttribution(header, viewReviewUrl) {
      const link = header.querySelector('[data-kiyoh-view-review-url]');
      const wrap = link?.closest('.reviews__kiyoh-attribution');
      if (!link || !wrap) return;

      if (viewReviewUrl) {
        link.href = viewReviewUrl;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        wrap.removeAttribute('hidden');
      } else {
        link.removeAttribute('href');
        link.removeAttribute('target');
        link.removeAttribute('rel');
        wrap.setAttribute('hidden', '');
      }
    }
  
    showLoading() {
      // Only show loading in the slider area now
      const slider = document.querySelector(`#Slider-${this.dataset.section}`);
      if (slider) {
        slider.innerHTML = `
          <div class="loading-state">
            <div class="loading-spinner"></div>
            <p>Loading reviews...</p>
          </div>
        `;
      }

      // Clear header titles
      const header = document.querySelector(`#ReviewsHeader-${this.dataset.section}`);
      if (header) {
        const countEl = header.querySelector('[data-kiyoh-title="count"]');
        const ratingEl = header.querySelector('[data-kiyoh-title="rating"]');
        if (countEl) countEl.textContent = '';
        if (ratingEl) ratingEl.textContent = '';
        this.#setKiyohAttribution(header, '');
      }
    }
  
    hideLoading() {
      const loadingState = document.querySelector(`#Slider-${this.dataset.section} .loading-state`);
      if (loadingState) {
        loadingState.remove();
      }
    }
  
    showError(message) {
      this.hideLoading();
      // Show error in slider area
      const slider = document.querySelector(`#Slider-${this.dataset.section}`);
      if (slider) {
        slider.innerHTML = `
          <div class="error-state">
            <p>${message}</p>
          </div>
        `;
      }
    }
  
    #render(reviews, summary) {
  
      // Get the existing slider
      const slider = document.querySelector(`#Slider-${this.dataset.section}`);
      if (!slider) {
        console.error('Slider not found:', `#Slider-${this.dataset.section}`);
        return;
      }
  
      const header = document.querySelector(`#ReviewsHeader-${this.dataset.section}`);
      if (header && summary) {
        const totalReviews = Number.parseInt(summary.totalReviews, 10);
        const averageRating10 = Number.parseFloat(summary.avgRating);
        const averageRating5 = averageRating10 / 2;

        const countEl = header.querySelector('[data-kiyoh-title="count"]');
        const ratingEl = header.querySelector('[data-kiyoh-title="rating"]');
        const countTemplate = header.dataset.titleCountTemplate || '';
        const ratingTemplate = header.dataset.titleRatingTemplate || '';

        const replaceTemplate = (template, values) => {
          if (!template) return '';

          return template.replace(/%(\w+)%/g, (match, key) => {
            const value = values[key];
            return value === undefined || value === null ? '' : String(value);
          });
        };

        if (countEl) {
          countEl.textContent = Number.isFinite(totalReviews)
            ? replaceTemplate(countTemplate, { count: totalReviews })
            : '';
        }

        if (ratingEl) {
          const formattedRating = Number.isFinite(averageRating5)
            ? averageRating5.toLocaleString('nl-NL', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
            : '';
          if (formattedRating) {
            const ratingText = replaceTemplate(ratingTemplate, { rating: formattedRating });
            // Wrap the "/" and maximum rating (e.g., "/5") in a <small> tag
            const formattedText = ratingText.replace(/(\/\d+)/g, '<small>$1</small>');
            ratingEl.innerHTML = formattedText;
          } else {
            ratingEl.textContent = '';
          }
        }

        this.#setKiyohAttribution(header, summary.viewReviewUrl || '');
      }

      // Ensure templates are available
      if (!this.reviewTemplate) {
        console.error('KiyohReviews: Review template not found.');
        return;
      }
  
      // Clear existing slides or loading state
      slider.innerHTML = '';
  
      // Add reviews as slides using the template
      reviews.forEach((review, index) => {
        const { author, city, date, rating, reviewId, oneliner, opinion } = review;
        const slideNode = this.reviewTemplate.cloneNode(true);
  
        // Set slide ID
        slideNode.id = `Slide-${this.dataset.section}-${index}`;
  
        // Replace modal ID placeholder with actual reviewId
        const modalIdBase = `review-modal-${this.dataset.section}-${reviewId}`;
        const modalOpener = slideNode.querySelector('modal-opener[data-modal]');
        const modalDialog = slideNode.querySelector('modal-dialog[id]');
        const popupButton = slideNode.querySelector('button[id^="ProductPopup-"]');
        const closeButton = slideNode.querySelector('button[id^="ModalClose-"]');
        
        if (modalOpener) {
          modalOpener.setAttribute('data-modal', `#PopupModal-${modalIdBase}`);
        }
        if (modalDialog) {
          modalDialog.id = `PopupModal-${modalIdBase}`;
        }
        if (popupButton) {
          popupButton.id = `ProductPopup-${modalIdBase}`;
        }
        if (closeButton) {
          closeButton.id = `ModalClose-${modalIdBase}`;
        }
  
        // Populate placeholders
        slideNode.querySelector('[data-review="authorCity"]').textContent = `${author} uit ${city}`;
        slideNode.querySelector('[data-review="oneliner"]').textContent = oneliner;
        slideNode.querySelector('[data-review="opinion"]').textContent = opinion;
        slideNode.querySelector('[data-review="summary"]').textContent = opinion;
        slideNode.querySelector('[data-review="date"]').textContent = date ? new Date(date).toLocaleDateString() : ''; // Format date
  
        // Calculate and set star rating percentage
        const starValue = (parseFloat(rating) / 2).toFixed(1); // Convert 10-point scale to 5-point scale
        const percentageStar = (starValue * 20).toFixed(0); // Convert to percentage
        
        const ratingValueRect = slideNode.querySelector('.rating__value');
        if (ratingValueRect) {
            ratingValueRect.setAttribute('width', `${percentageStar}%`);
        } else {
            // New star logic using pre-rendered icons
            const ratingContainer = slideNode.querySelector('[data-rating-container]');
            if (ratingContainer) {
                const ratingOutOf10 = parseFloat(rating);
                const ratingOutOf5 = ratingOutOf10 / 2.0;
                const fullStars = Math.floor(ratingOutOf5);
                const hasHalfStar = (ratingOutOf5 % 1) >= 0.4;

                const starIcons = ratingContainer.querySelectorAll('.star-icon');
                starIcons.forEach((star, index) => {
                    star.classList.remove('star--full', 'star--half', 'star--empty'); // Reset classes
                    if (index < fullStars) {
                        star.classList.add('star--full');
                    } else if (index === fullStars && hasHalfStar) {
                        star.classList.add('star--half');
                    } else {
                        star.classList.add('star--empty');
                    }
                });
            }
        }
  
        slider.appendChild(slideNode);
      });
  
      // Reinitialize the slider
      const sliderComponent = slider.closest('slider-component');
      if (sliderComponent) {
        console.log('Reinitializing slider with', reviews.length, 'slides');

        // Wait for DOM + layout to settle before recalculating pages
        requestAnimationFrame(() => {
          // Guard against sections that don't render slider controls
          const hasControls = sliderComponent.prevButton && sliderComponent.nextButton;
          const canReset = typeof sliderComponent.resetPages === 'function';
          if (!hasControls || !canReset) return;

          sliderComponent.resetPages();
        });
      } else if (reviews.length > 0) { // Only log error if reviews were expected
        console.error('Slider component not found');
      }
    }
  }
  
  customElements.define('kiyoh-reviews', KiyohReviews);